import { useEffect, useState } from 'react';
import PricingPage from './pages/PricingPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import LayoutWrapper from './components/LayoutWrapper';
import { supabase } from './lib/supabase-client';
import { ProtectedRoute } from './components/auth/ProtectedRoute';

import HomePage from './pages/Home';
import LoginPage from './pages/Login';
import SignupPage from './pages/Signup';
import RecruiterSignupPage from './pages/RecruiterSignup';
import DashboardPage from './pages/Dashboard';
import JobsPage from './pages/Jobs';
import JobDetailPage from './pages/JobDetail';
import JobCreatePage from './pages/JobCreate';
import JobEditPage from './pages/JobEdit';
import InterviewPage from './pages/Interview';
import InterviewSessionPage from './pages/InterviewSession';
import InterviewsListPage from './pages/InterviewsList';
import InterviewDetailPage from './pages/InterviewDetail';
import ProfilePage from './pages/Profile';
import SettingsPage from './pages/Settings';
import CandidatesPage from './pages/CandidatesPage';
import CandidateDetailPage from './pages/CandidateDetailPage';

function EmailConfirmationHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const error = params.get('error');
      const errorDescription = params.get('error_description');

      if (error) {
        console.error('[EmailConfirmation] Error from Supabase:', error, errorDescription);
        navigate('/login?error=' + encodeURIComponent(errorDescription || 'Confirmation failed'));
        return;
      }

      if (code && !processing && !user) {
        setProcessing(true);
        console.log('[EmailConfirmation] Exchanging code for session...');

        try {
          const { data, error: exchangeError} = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('[EmailConfirmation] Exchange error:', exchangeError);
            navigate('/login?error=' + encodeURIComponent('Email confirmation failed. Please try signing in.'));
            return;
          }

          if (data?.session) {
            console.log('[EmailConfirmation] Session obtained, redirecting to dashboard...');
            navigate('/dashboard', { replace: true });
          }
        } catch (err) {
          console.error('[EmailConfirmation] Exception:', err);
          navigate('/login?error=' + encodeURIComponent('An error occurred. Please try signing in.'));
        }
      }
    };

    if (!loading) {
      handleEmailConfirmation();
    }
  }, [location, navigate, user, loading, processing]);

  return null;
}

function RootRedirect() {
  const { user, loading } = useAuth();

  console.log('[RootRedirect] 🏠 Render:', { loading, hasUser: !!user });

  if (loading) {
    console.log('[RootRedirect] ⏳ Loading...');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const redirectTo = user ? '/dashboard' : '/login';
  console.log('[RootRedirect] ➡️ Redirecting to:', redirectTo);
  return user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
}

function AppContent() {
  return (
    <>
      <EmailConfirmationHandler />
      <LayoutWrapper>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/signup/recruiter" element={<RecruiterSignupPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
          <Route path="/jobs/create" element={<ProtectedRoute><JobCreatePage /></ProtectedRoute>} />
          <Route path="/jobs/:id" element={<JobDetailPage />} />
          <Route path="/jobs/:id/edit" element={<ProtectedRoute><JobEditPage /></ProtectedRoute>} />
          <Route path="/interview" element={<ProtectedRoute><InterviewPage /></ProtectedRoute>} />
          <Route path="/interview/session" element={<ProtectedRoute><InterviewSessionPage /></ProtectedRoute>} />
          <Route path="/interviews" element={<ProtectedRoute><InterviewsListPage /></ProtectedRoute>} />
          <Route path="/interviews/:id" element={<ProtectedRoute><InterviewDetailPage /></ProtectedRoute>} />
          <Route path="/candidates" element={<ProtectedRoute><CandidatesPage /></ProtectedRoute>} />
          <Route path="/candidates/:sessionId" element={<ProtectedRoute><CandidateDetailPage /></ProtectedRoute>} />
          <Route path="/profile/:userId" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
          <Route path="/checkout/success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </LayoutWrapper>
      <Toaster />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
